import React, { useState, useEffect, useRef, useCallback } from 'react';
import GameCard from './GameCard';
import './GameGrid.css';

const GAMES_PER_PAGE = 24;

function GameGrid({ games, onGamePlay }) {
  const [displayedGames, setDisplayedGames] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const observerTarget = useRef(null);

  // Reset displayed games when games array changes (filter/search)
  useEffect(() => {
    // Show all games if less than or equal to GAMES_PER_PAGE, otherwise show first batch
    const initialGames = games.length <= GAMES_PER_PAGE ? games : games.slice(0, GAMES_PER_PAGE);
    setDisplayedGames(initialGames);
    setHasMore(games.length > GAMES_PER_PAGE);
  }, [games]);

  // Load more games
  const loadMore = useCallback(() => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    
    // Simulate slight delay for smooth UX
    setTimeout(() => {
      const currentLength = displayedGames.length;
      const nextGames = games.slice(currentLength, currentLength + GAMES_PER_PAGE);
      
      setDisplayedGames(prev => [...prev, ...nextGames]);
      setHasMore(currentLength + GAMES_PER_PAGE < games.length);
      setIsLoading(false);
    }, 300);
  }, [games, displayedGames.length, hasMore, isLoading]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoading, loadMore]);

  if (games.length === 0) {
    return (
      <section className="game-grid-container" aria-label="Casino games">
        <div className="no-games-message">
          <p>No games found. Try adjusting your search or filters.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="game-grid-container" aria-label="Casino games">
      <div className="game-grid" role="list">
        {displayedGames.map((game, index) => (
          <article key={game._id || game.slug || index} role="listitem">
            <GameCard game={game} onPlay={onGamePlay} />
          </article>
        ))}
      </div>
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading more games...</p>
        </div>
      )}
      
      {/* Intersection observer target */}
      {hasMore && !isLoading && (
        <div ref={observerTarget} className="load-more-trigger" />
      )}
      
      {/* End of results message */}
      {!hasMore && displayedGames.length > 0 && (
        <div className="end-message">
          <p>
            {games.length === 1 
              ? "That's the only game matching your search!" 
              : `You've reached the end! Showing all ${games.length} games.`
            }
          </p>
        </div>
      )}
    </section>
  );
}

export default GameGrid;
